import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, formatMoney, PAYMENT_METHODS, PAYMENT_LABEL } from "@/lib/api";
import { Plus, Lock, LockOpen, Trash2, History, FileText, Download, RotateCcw, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { generateCashboxPdf } from "@/lib/cashboxPdf";
import { useBranding } from "@/context/BrandingContext";

const CATEGORIES = ["insumos", "servicios", "nómina", "alquiler", "marketing", "general"];

function StatTile({ label, value, accent }) {
  return (
    <div className={`p-5 sm:p-6 border-r border-b border-border last:border-r-0 ${accent || ""}`}>
      <div className="label-eyebrow">{label}</div>
      <div className="font-mono font-bold text-3xl sm:text-4xl tracking-tight mt-2">{value}</div>
    </div>
  );
}

function todayStr() { return new Date().toISOString().slice(0, 10); }
function firstOfMonth() { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); }
function fmtTimeShort(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" });
}

export default function Caja() {
  const [tab, setTab] = useState("actual");
  return (
    <div className="space-y-6" data-testid="caja-page">
      <div className="flex items-center gap-1 border-b border-border">
        <button
          onClick={() => setTab("actual")}
          data-testid="caja-tab-actual"
          className={`px-5 h-11 text-sm font-semibold inline-flex items-center gap-2 border-b-2 -mb-px transition-colors ${tab === "actual" ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
          <LockOpen className="w-4 h-4" /> Sesión actual
        </button>
        <button
          onClick={() => setTab("historial")}
          data-testid="caja-tab-historial"
          className={`px-5 h-11 text-sm font-semibold inline-flex items-center gap-2 border-b-2 -mb-px transition-colors ${tab === "historial" ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
          <History className="w-4 h-4" /> Historial
        </button>
      </div>
      {tab === "actual" ? <CajaActual /> : <CajaHistorial />}
    </div>
  );
}

function CajaActual() {
  const { branding } = useBranding();
  const [data, setData] = useState(null);
  const [openingAmt, setOpeningAmt] = useState("");
  const [counted, setCounted] = useState({});
  const [closeNotes, setCloseNotes] = useState("");
  const [tx, setTx] = useState({ type: "ingreso", amount: "", category: "general", description: "", payment_method: "efectivo" });

  const load = async () => {
    const { data } = await api.get("/cashbox/today");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-sm text-muted-foreground">Cargando…</div>;
  const { session, txns, summary } = data;
  const isOpen = session && session.status === "abierta";
  const isClosed = session && session.status === "cerrada";
  const noSession = !session;

  const openCash = async () => {
    try { await api.post("/cashbox/open", { opening_amount: Number(openingAmt) || 0 }); toast.success("Caja abierta"); setOpeningAmt(""); setCounted({}); setCloseNotes(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const closeCash = async () => {
    const counted_by_method = {};
    for (const [k, v] of Object.entries(counted)) {
      if (v !== "" && v != null) counted_by_method[k] = Number(v) || 0;
    }
    try {
      const { data: res } = await api.post("/cashbox/close", { counted_by_method, notes: closeNotes });
      toast.success("Caja cerrada");
      setCloseNotes("");
      // Auto-descarga del PDF de comprobante
      try {
        const det = await api.get(`/cashbox/sessions/${res.session.id}`);
        generateCashboxPdf({ ...det.data, branding });
      } catch {}
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const addTx = async (e) => {
    e.preventDefault();
    if (!tx.amount) return;
    try {
      await api.post("/cashbox/txns", { ...tx, amount: Number(tx.amount) });
      setTx({ type: "ingreso", amount: "", category: "general", description: "", payment_method: "efectivo" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const delTx = async (id) => { try { await api.delete(`/cashbox/txns/${id}`); load(); } catch { toast.error("No se pudo eliminar"); } };

  const downloadPdf = async () => {
    try {
      const { data: det } = await api.get(`/cashbox/sessions/${session.id}`);
      generateCashboxPdf({ ...det, branding });
    } catch (e) { toast.error("No se pudo generar el PDF"); }
  };

  const today = new Date().toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });
  const sessionLabel = session?.session_number ? ` · Sesión #${session.session_number}` : "";

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2">Caja del día · {today}{sessionLabel}</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-none">
            {noSession ? "Caja sin abrir" : isOpen ? "Caja abierta" : "Caja cerrada"}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {session && (
            <button onClick={downloadPdf} className="px-4 h-10 rounded-sm border border-border hover:bg-muted text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2" data-testid="download-pdf-btn">
              <FileText className="w-4 h-4" /> Comprobante PDF
            </button>
          )}
          <div className={`flex items-center gap-2 px-3 h-10 rounded-sm border ${isOpen ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>
            {isOpen ? <LockOpen className="w-4 h-4"/> : <Lock className="w-4 h-4"/>}
            <span className="text-xs uppercase tracking-widest font-bold">
              {isOpen ? "Sesión activa" : isClosed ? "Sesión cerrada" : "Sin sesión"}
            </span>
          </div>
        </div>
      </header>

      {/* Permitir abrir caja: si nunca abrió, O si la última está cerrada (nueva sesión del día) */}
      {(noSession || isClosed) && (
        <section className="card-flat p-6">
          <h2 className="font-display font-bold text-2xl tracking-tight">
            {isClosed ? "Abrir nueva caja" : "Apertura de caja"}
          </h2>
          <p className="text-sm text-muted-foreground mt-1 mb-4">
            {isClosed
              ? "La sesión anterior está cerrada. Puedes abrir una nueva caja (turno) si lo necesitas."
              : "Registra el monto inicial con el que comienzas el día."}
          </p>
          <div className="flex flex-wrap gap-3">
            <input
              data-testid="opening-amount"
              type="number" step="1000" placeholder="Monto inicial"
              value={openingAmt} onChange={(e) => setOpeningAmt(e.target.value)}
              className="input-flat font-mono w-44"
            />
            <button data-testid="open-cash-btn" onClick={openCash} className="btn-primary">
              <LockOpen className="w-4 h-4" /> {isClosed ? "Abrir nueva sesión" : "Abrir caja"}
            </button>
          </div>
        </section>
      )}

      {session && (
        <>
          <section className="card-flat overflow-hidden">
            <div className="grid grid-cols-2 sm:grid-cols-4">
              <StatTile label="Apertura" value={formatMoney(summary.opening)} />
              <StatTile label="Ingresos" value={formatMoney(summary.income)} accent="bg-primary/5" />
              <StatTile label="Gastos" value={formatMoney(summary.expense)} accent="bg-destructive/5" />
              <StatTile label="Esperado total" value={formatMoney(summary.expected)} accent="bg-accent/10" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-2 border-t border-border">
              <StatTile label="Efectivo en caja (debe coincidir)" value={formatMoney(summary.cash_expected)} accent="bg-primary/10" />
              <StatTile label="Otros métodos (digital)" value={formatMoney((summary.income || 0) - (summary.cash_income || 0))} accent="bg-muted/50" />
            </div>
            {summary.counted != null && (
              <div className="grid grid-cols-2 sm:grid-cols-2 border-t border-border">
                <StatTile label="Contado" value={formatMoney(summary.counted)} />
                <StatTile label="Diferencia" value={formatMoney(summary.difference)} accent={summary.difference !== 0 ? "bg-destructive/10" : "bg-primary/5"} />
              </div>
            )}
          </section>

          {isOpen && (
            <section className="card-flat p-5">
              <h2 className="font-display font-bold text-xl tracking-tight mb-4">Nuevo movimiento</h2>
              <form onSubmit={addTx} className="grid grid-cols-2 sm:grid-cols-6 gap-3" data-testid="txn-form">
                <select className="input-flat" value={tx.type} onChange={(e) => setTx(t => ({...t, type: e.target.value}))}>
                  <option value="ingreso">Ingreso</option>
                  <option value="gasto">Gasto</option>
                  <option value="ajuste">Ajuste</option>
                </select>
                <input data-testid="txn-amount" required type="number" step="1000" placeholder="Monto" className="input-flat font-mono" value={tx.amount} onChange={(e) => setTx(t => ({...t, amount: e.target.value}))} />
                <select className="input-flat" value={tx.category} onChange={(e) => setTx(t => ({...t, category: e.target.value}))}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select className="input-flat" value={tx.payment_method} onChange={(e) => setTx(t => ({...t, payment_method: e.target.value}))}>
                  {PAYMENT_METHODS.map((m) => <option key={m.key} value={m.key}>{m.short}</option>)}
                </select>
                <input className="input-flat sm:col-span-1" placeholder="Descripción" value={tx.description} onChange={(e) => setTx(t => ({...t, description: e.target.value}))} />
                <button data-testid="add-txn-btn" type="submit" className="btn-primary justify-center">
                  <Plus className="w-4 h-4" /> Añadir
                </button>
              </form>
            </section>
          )}

          <section className="card-flat overflow-hidden">
            <div className="grid grid-cols-12 px-4 py-3 border-b border-border bg-muted/40">
              <div className="col-span-2 label-eyebrow">Hora</div>
              <div className="col-span-2 label-eyebrow">Tipo</div>
              <div className="col-span-3 label-eyebrow">Descripción</div>
              <div className="col-span-1 label-eyebrow">Cat.</div>
              <div className="col-span-1 label-eyebrow">Pago</div>
              <div className="col-span-2 label-eyebrow text-right">Monto</div>
              <div className="col-span-1"></div>
            </div>
            {(txns || []).length === 0 && (
              <div className="px-4 py-12 text-center text-sm text-muted-foreground">Sin movimientos en la caja todavía.</div>
            )}
            {(txns || []).map((t) => (
              <div key={t.id} className={`grid grid-cols-12 px-4 py-3 border-b border-border last:border-b-0 hover:bg-muted/40 stripe-${t.type === "ingreso" ? "confirmada" : t.type === "gasto" ? "cancelada" : "pendiente"}`} data-testid={`txn-row-${t.id}`}>
                <div className="col-span-2 font-mono text-xs text-muted-foreground">{fmtTimeShort(t.created_at)}</div>
                <div className="col-span-2 text-xs uppercase tracking-widest font-bold">{t.type}</div>
                <div className="col-span-3 text-sm truncate">{t.description || "—"}</div>
                <div className="col-span-1 text-xs text-muted-foreground truncate">{t.category}</div>
                <div className="col-span-1 text-[10px] uppercase tracking-widest font-bold text-muted-foreground truncate">
                  {PAYMENT_METHODS.find(m => m.key === t.payment_method)?.short || "—"}
                </div>
                <div className={`col-span-2 text-right font-mono text-sm ${t.type === "gasto" ? "text-destructive" : ""}`}>
                  {t.type === "gasto" ? "-" : "+"}{formatMoney(t.amount)}
                </div>
                <div className="col-span-1 flex justify-end">
                  {isOpen && (
                    <button onClick={() => delTx(t.id)} className="p-2 text-muted-foreground hover:text-destructive">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </section>

          {isOpen && (
            <CierreCaja
              summary={summary}
              counted={counted}
              setCounted={setCounted}
              closeNotes={closeNotes}
              setCloseNotes={setCloseNotes}
              onClose={closeCash}
            />
          )}
        </>
      )}
    </div>
  );
}

function CierreCaja({ summary, counted, setCounted, closeNotes, setCloseNotes, onClose }) {
  const expectedByMethod = summary.expected_by_method || {};
  const methods = Array.from(
    new Set(["efectivo", ...Object.keys(expectedByMethod).filter((m) => Math.abs(expectedByMethod[m] || 0) > 0)])
  );

  const rows = methods.map((m) => {
    const meta = PAYMENT_METHODS.find((x) => x.key === m) || { key: m, label: m, short: m, bg: "#888", fg: "#fff" };
    const exp = Number(expectedByMethod[m] || 0);
    const raw = counted[m];
    const isEntered = raw !== "" && raw != null;
    const c = Number(raw) || 0;
    const diff = c - exp;
    const isExact = isEntered && Math.abs(diff) < 0.01;
    return { method: m, meta, expected: exp, counted: raw, isEntered, c, diff, isExact };
  });

  const allEntered = rows.every((r) => r.isEntered);
  const hasDiff = rows.some((r) => r.isEntered && !r.isExact);
  const allMatch = allEntered && !hasDiff;
  const canClose = allEntered && (allMatch || closeNotes.trim().length > 0);

  const totalCounted = rows.reduce((s, r) => s + (r.c || 0), 0);
  const totalExpected = rows.reduce((s, r) => s + r.expected, 0);

  const setMethodValue = (m, v) => setCounted((prev) => ({ ...prev, [m]: v }));

  return (
    <section className="card-flat p-5 sm:p-6 space-y-5" data-testid="cierre-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-bold text-xl tracking-tight">Cierre de caja por método de pago</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Cuadra cada método recibido durante el día. La caja solo puede cerrarse si todo coincide o si registras una justificación de la diferencia.
          </p>
        </div>
        <div className="text-right">
          <div className="label-eyebrow">Total esperado</div>
          <div className="font-mono font-bold text-2xl tracking-tight text-primary mt-1">{formatMoney(totalExpected)}</div>
        </div>
      </div>

      <div className="space-y-2">
        {rows.map((r) => (
          <div
            key={r.method}
            className={`grid grid-cols-12 gap-3 items-center px-3 sm:px-4 py-3 rounded-sm border ${
              !r.isEntered ? "border-border bg-card" :
              r.isExact ? "border-primary/40 bg-primary/5" :
              r.diff > 0 ? "border-accent/40 bg-accent/5" :
              "border-destructive/40 bg-destructive/5"
            }`}
            data-testid={`cierre-row-${r.method}`}
          >
            <div className="col-span-12 sm:col-span-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-sm flex items-center justify-center text-[10px] font-bold uppercase tracking-widest" style={{ background: r.meta.bg, color: r.meta.fg }}>
                {r.meta.short.slice(0, 3)}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-bold truncate">{r.meta.label}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">esperado</div>
              </div>
            </div>
            <div className="col-span-4 sm:col-span-2 font-mono font-bold text-base text-foreground">
              {formatMoney(r.expected)}
            </div>
            <div className="col-span-8 sm:col-span-3">
              <div className="label-eyebrow mb-1">Contado</div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm text-muted-foreground">$</span>
                <input
                  type="number" step="1000" placeholder="0"
                  value={r.counted ?? ""}
                  onChange={(e) => setMethodValue(r.method, e.target.value)}
                  className="input-flat font-mono text-base !h-10 font-bold"
                  data-testid={`cierre-input-${r.method}`}
                />
              </div>
            </div>
            <div className="col-span-7 sm:col-span-3">
              <div className="label-eyebrow mb-1">Diferencia</div>
              <div className={`h-10 px-3 rounded-sm border flex items-center font-mono font-bold ${
                !r.isEntered ? "border-border text-muted-foreground" :
                r.isExact ? "border-primary text-primary" :
                r.diff > 0 ? "border-accent text-accent" :
                "border-destructive text-destructive"
              }`}>
                {!r.isEntered ? "—" : (r.diff >= 0 ? "+" : "") + formatMoney(r.diff)}
              </div>
            </div>
            <div className="col-span-5 sm:col-span-1 flex justify-end">
              {r.isEntered && (r.isExact ? (
                <div className="w-8 h-8 rounded-sm bg-primary text-primary-foreground flex items-center justify-center font-bold" data-testid={`cierre-ok-${r.method}`}>✓</div>
              ) : (
                <div className="w-8 h-8 rounded-sm bg-destructive text-destructive-foreground flex items-center justify-center font-bold" data-testid={`cierre-bad-${r.method}`}>!</div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between p-4 rounded-sm bg-muted/40 border border-border">
        <div>
          <div className="label-eyebrow">Total contado</div>
          <div className="font-mono font-bold text-2xl tracking-tight mt-1">{formatMoney(totalCounted)}</div>
        </div>
        <div className="text-right">
          <div className="label-eyebrow">Diferencia total</div>
          <div className={`font-mono font-bold text-2xl tracking-tight mt-1 ${allMatch ? "text-primary" : "text-destructive"}`}>
            {(totalCounted - totalExpected) >= 0 ? "+" : ""}{formatMoney(totalCounted - totalExpected)}
          </div>
        </div>
      </div>

      {hasDiff && (
        <div className="border border-destructive/40 bg-destructive/5 rounded-sm p-4 space-y-3" data-testid="justification-block">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-sm bg-destructive text-destructive-foreground flex items-center justify-center font-bold text-sm shrink-0">!</div>
            <div className="flex-1">
              <div className="font-bold text-sm">El cuadre no coincide en algunos métodos</div>
              <div className="text-xs text-muted-foreground">
                Para cerrar la caja debes registrar una justificación de las diferencias detectadas.
              </div>
            </div>
          </div>
          <textarea
            data-testid="close-notes" rows={3} value={closeNotes}
            onChange={(e) => setCloseNotes(e.target.value)}
            placeholder="Justifica las diferencias: ej. cambio dado de más, propina, billete faltante, transacción Nequi pendiente, error de digitación…"
            className="input-flat h-auto py-2 text-sm w-full"
          />
        </div>
      )}

      {allMatch && (
        <div className="border border-primary/40 bg-primary/5 rounded-sm p-3 flex items-center gap-3 text-sm" data-testid="exact-block">
          <div className="w-7 h-7 rounded-sm bg-primary text-primary-foreground flex items-center justify-center font-bold">✓</div>
          <div className="font-semibold">Todos los métodos cuadrados perfectamente. Lista para cerrar.</div>
        </div>
      )}

      <div className="flex justify-end">
        <button
          data-testid="close-cash-btn" onClick={onClose} disabled={!canClose}
          className="btn-primary !h-12 !px-6 disabled:opacity-50 disabled:cursor-not-allowed">
          <Lock className="w-5 h-5" /> Cerrar caja y descargar comprobante
        </button>
      </div>
    </section>
  );
}

function CajaHistorial() {
  const { branding } = useBranding();
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(todayStr());
  const [sessions, setSessions] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [detailCache, setDetailCache] = useState({});
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cashbox/sessions", { params: { date_from: from, date_to: to } });
      setSessions(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [from, to]); // eslint-disable-line

  const expand = async (sid) => {
    if (expanded === sid) { setExpanded(null); return; }
    setExpanded(sid);
    if (!detailCache[sid]) {
      try {
        const { data } = await api.get(`/cashbox/sessions/${sid}`);
        setDetailCache((c) => ({ ...c, [sid]: data }));
      } catch (e) { toast.error("No se pudo cargar el detalle"); }
    }
  };

  const downloadPdf = async (sid) => {
    try {
      const det = detailCache[sid] || (await api.get(`/cashbox/sessions/${sid}`)).data;
      generateCashboxPdf({ ...det, branding });
    } catch (e) { toast.error("No se pudo generar el PDF"); }
  };

  const totals = sessions.reduce((acc, s) => {
    const sum = s.summary || {};
    acc.income += sum.income || 0;
    acc.expense += sum.expense || 0;
    acc.diff += sum.difference || 0;
    return acc;
  }, { income: 0, expense: 0, diff: 0 });

  return (
    <div className="space-y-5" data-testid="caja-historial">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2">Cajas anteriores</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter leading-none">Historial de sesiones</h1>
          <p className="text-muted-foreground text-sm mt-2">Consulta cualquier cierre, descarga el comprobante para firma.</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <div className="label-eyebrow mb-1">Desde</div>
            <input type="date" className="input-flat font-mono w-40" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="hist-from" />
          </div>
          <div>
            <div className="label-eyebrow mb-1">Hasta</div>
            <input type="date" className="input-flat font-mono w-40" value={to} onChange={(e) => setTo(e.target.value)} data-testid="hist-to" />
          </div>
          <button onClick={load} className="px-3 h-10 rounded-sm border border-border hover:bg-muted text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2">
            <RotateCcw className="w-4 h-4" /> Actualizar
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div className="card-flat p-4">
          <div className="label-eyebrow">Sesiones</div>
          <div className="font-mono font-bold text-3xl mt-1">{sessions.length}</div>
        </div>
        <div className="card-flat p-4">
          <div className="label-eyebrow">Ingresos totales</div>
          <div className="font-mono font-bold text-3xl mt-1 text-primary">{formatMoney(totals.income)}</div>
        </div>
        <div className="card-flat p-4">
          <div className="label-eyebrow">Gastos totales</div>
          <div className="font-mono font-bold text-3xl mt-1 text-destructive">{formatMoney(totals.expense)}</div>
        </div>
        <div className="card-flat p-4">
          <div className="label-eyebrow">Diferencia acumulada</div>
          <div className={`font-mono font-bold text-3xl mt-1 ${totals.diff < 0 ? "text-destructive" : ""}`}>{formatMoney(totals.diff)}</div>
        </div>
      </div>

      <div className="card-flat overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-3 border-b border-border bg-muted/40 text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
          <div className="col-span-2">Fecha</div>
          <div className="col-span-1">#</div>
          <div className="col-span-2">Apertura → Cierre</div>
          <div className="col-span-2">Responsable</div>
          <div className="col-span-1 text-right">Ingresos</div>
          <div className="col-span-1 text-right">Gastos</div>
          <div className="col-span-1 text-right">Diferencia</div>
          <div className="col-span-1">Estado</div>
          <div className="col-span-1 text-right">Acciones</div>
        </div>
        {loading ? (
          <div className="px-4 py-12 text-center text-sm text-muted-foreground">Cargando…</div>
        ) : sessions.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-muted-foreground">No hay sesiones en el período.</div>
        ) : sessions.map((s) => {
          const sum = s.summary || {};
          const diff = sum.difference;
          const isExp = expanded === s.id;
          return (
            <div key={s.id} data-testid={`session-row-${s.id}`}>
              <button
                onClick={() => expand(s.id)}
                className="w-full grid grid-cols-12 px-4 py-3 border-b border-border items-center hover:bg-muted/40 text-left">
                <div className="col-span-2 font-mono text-xs">{s.date}</div>
                <div className="col-span-1 text-xs font-bold">#{s.session_number || 1}</div>
                <div className="col-span-2 font-mono text-xs text-muted-foreground">
                  {fmtTimeShort(s.opened_at)} → {s.closed_at ? fmtTimeShort(s.closed_at) : "—"}
                </div>
                <div className="col-span-2 text-sm truncate">{s.opened_by_user?.name || "—"}</div>
                <div className="col-span-1 text-right font-mono text-sm text-primary">{formatMoney(sum.income || 0)}</div>
                <div className="col-span-1 text-right font-mono text-sm text-destructive">{formatMoney(sum.expense || 0)}</div>
                <div className={`col-span-1 text-right font-mono text-sm font-bold ${diff && Math.abs(diff) > 0.01 ? "text-destructive" : ""}`}>
                  {diff != null ? `${diff >= 0 ? "+" : ""}${formatMoney(diff)}` : "—"}
                </div>
                <div className="col-span-1">
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-sm ${s.status === "abierta" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                    {s.status}
                  </span>
                </div>
                <div className="col-span-1 flex justify-end items-center gap-1">
                  <span
                    onClick={(e) => { e.stopPropagation(); downloadPdf(s.id); }}
                    role="button" tabIndex={0}
                    data-testid={`pdf-btn-${s.id}`}
                    title="Descargar comprobante PDF"
                    className="p-2 rounded-sm hover:bg-primary/10 text-primary cursor-pointer">
                    <Download className="w-4 h-4" />
                  </span>
                  <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${isExp ? "rotate-90" : ""}`} />
                </div>
              </button>
              {isExp && (
                <SessionDetail data={detailCache[s.id]} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SessionDetail({ data }) {
  if (!data) return <div className="px-4 py-6 text-sm text-muted-foreground bg-muted/20">Cargando detalle…</div>;
  const { session, txns, summary } = data;
  const exp = summary.expected_by_method || {};
  const cnt = summary.counted_by_method || {};
  const methods = Object.keys(exp);

  return (
    <div className="bg-muted/20 px-4 py-5 border-b border-border space-y-4" data-testid="session-detail">
      {methods.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {methods.map((m) => {
            const e = Number(exp[m] || 0);
            const c = Number(cnt[m] || 0);
            const d = c - e;
            const ok = Math.abs(d) < 0.01;
            return (
              <div key={m} className={`p-3 rounded-sm border ${ok ? "border-primary/30 bg-card" : "border-destructive/40 bg-destructive/5"}`}>
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{PAYMENT_LABEL[m] || m}</div>
                <div className="flex items-center justify-between mt-1">
                  <div className="font-mono text-xs text-muted-foreground">esp {formatMoney(e)}</div>
                  <div className="font-mono text-xs">cnt {formatMoney(c)}</div>
                </div>
                <div className={`font-mono font-bold text-sm mt-1 ${ok ? "text-primary" : "text-destructive"}`}>
                  {d >= 0 ? "+" : ""}{formatMoney(d)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {session.closing_notes && (
        <div className="border border-destructive/30 bg-destructive/5 rounded-sm p-3">
          <div className="label-eyebrow text-destructive mb-1">Justificación del cierre</div>
          <p className="text-sm">{session.closing_notes}</p>
        </div>
      )}

      <div className="border border-border rounded-sm overflow-hidden">
        <div className="grid grid-cols-12 px-3 py-2 bg-card border-b border-border text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
          <div className="col-span-2">Hora</div>
          <div className="col-span-2">Tipo</div>
          <div className="col-span-4">Descripción</div>
          <div className="col-span-2">Método</div>
          <div className="col-span-2 text-right">Monto</div>
        </div>
        {(txns || []).length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-muted-foreground bg-card">Sin movimientos.</div>
        ) : txns.map((t) => (
          <div key={t.id} className="grid grid-cols-12 px-3 py-2 border-b border-border last:border-b-0 bg-card text-xs">
            <div className="col-span-2 font-mono text-muted-foreground">{fmtTimeShort(t.created_at)}</div>
            <div className="col-span-2 uppercase tracking-widest font-bold">{t.type}</div>
            <div className="col-span-4 truncate">{t.description || "—"}</div>
            <div className="col-span-2 text-muted-foreground">{PAYMENT_LABEL[t.payment_method] || "—"}</div>
            <div className={`col-span-2 text-right font-mono font-bold ${t.type === "gasto" ? "text-destructive" : ""}`}>
              {t.type === "gasto" ? "-" : "+"}{formatMoney(t.amount)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
